import { isEqual, uniq, uniqBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useActiveMapTool } from './hooks/useActiveMapTool';
import { useClusteringState } from './hooks/useClusteringState';

import { useEntityDrawerState } from './hooks/useEntityDrawer';
import { useEntitySelections } from './hooks/useEntitySelections';
import { useStatusBarState } from './hooks/useStatusBar';
import { KineticLayer, MapContextData } from './MapContext';
import { Layer } from '@deck.gl/core/typed';
import { ContextMenuItem } from '../MapMarker';
import { FeatureCollection, Geometry } from '@turf/helpers';
import booleanIntersects from '@turf/boolean-intersects';
import { useLatest } from '@qfa/react-components';

export const useMapContextState = (hasEntityDrawer: boolean, initialLayers: string[]) => {
  const [layers, setLayers] = useState<KineticLayer[]>([]);
  const layersRef = useLatest(layers);
  const [extraDeckGlLayers, setExtraDeckGlLayers] = useState<Layer[]>([]);
  const extraDeckGlLayersRef = useLatest(extraDeckGlLayers);
  const { isStatusBarOpen, toggleStatusBar } = useStatusBarState();
  const { clusteringEnabled } = useClusteringState();
  const { activeMapTool, setActiveMapTool, cursor } = useActiveMapTool();
  // TODO: move these 2 hooks into their own context - EntityDrawerContext
  const { isEntitiesOpen, toggleEntities, openEntities } = useEntityDrawerState();
  const {
    isEntitySelected,
    isAnyEntitySelected,
    hasSelectedEntities,
    selectedEntities,
    setEntities,
    removeEntities,
    removeAllEntities,
    setEntity,
    selectedEntitiesCount,
    setSelectedEntities,
  } = useEntitySelections();

  const [activeStatusBar, setActiveStatusBar] = useState<string>('');

  // Option for markers that should not render on the map
  const [hiddenMarkers, setHiddenMarkers] = useState<string[]>([]);

  const [additionalMapContextMenuItems, setAdditionalMapContextMenuItems] = useState<
    ContextMenuItem[]
  >([]);

  const [selectedLayers, setSelectedLayers] = useState<string[]>(initialLayers || []);

  const setLayerSelected = useCallback((id: string, checked: boolean) => {
    if (checked) {
      setSelectedLayers((prev) => uniq([...prev, id]));
    } else {
      setSelectedLayers((prev) => prev.filter((listItem) => listItem !== id));
    }
  }, []);

  const [clickableMarkers, setClickableMarkers] = useState<boolean>(true);

  const registerLayer = useCallback(
    (layer: KineticLayer) => {
      // prevent repeated calls with the same layer data from causing re-renders
      const exists = layersRef.current.find((l) => l.id === layer.id);
      if (exists && isEqual(exists, layer)) {
        return;
      }
      setLayers((prev) => uniqBy([...prev, layer], 'id'));
    },
    [layersRef]
  );

  const intersectsWithExtraDeckGlData = useCallback(
    (geometry: Geometry): string[] => {
      const intersectingIds: string[] = [];

      extraDeckGlLayersRef.current.forEach((value) => {
        const featureCollection = value.props?.data as FeatureCollection;
        if (featureCollection?.features?.length) {
          featureCollection.features.forEach((feature) => {
            const intersection = booleanIntersects(feature, geometry);
            if (intersection && feature.properties?.id) {
              intersectingIds.push(feature.properties.id);
            }
          });
        }
      });

      return intersectingIds;
    },
    [extraDeckGlLayersRef]
  );

  const contextValue = useMemo(
    () => ({
      registerLayer,
      layers,
      setLayerSelected,
      selectedLayers,
      isEntitySelected,
      isAnyEntitySelected,
      hasSelectedEntities,
      selectedEntities,
      setSelectedEntities,
      setEntities,
      removeEntities,
      removeAllEntities,
      setEntity,
      selectedEntitiesCount,
      setSelectedLayers,
      isEntitiesOpen,
      toggleEntities,
      openEntities,
      setClickableMarkers,
      clickableMarkers,
      activeMapTool,
      setActiveMapTool,
      clusteringEnabled,
      isStatusBarOpen,
      toggleStatusBar,
      activeStatusBar,
      setActiveStatusBar,

      setExtraDeckGlLayers,
      cursor,
      hiddenMarkers,
      setHiddenMarkers,
      hasEntityDrawer,
      additionalMapContextMenuItems,
      setAdditionalMapContextMenuItems,
      intersectsWithExtraDeckGlData,
    }),
    [
      registerLayer,
      layers,
      setLayerSelected,
      selectedLayers,
      isEntitySelected,
      isAnyEntitySelected,
      hasSelectedEntities,
      selectedEntities,
      setSelectedEntities,
      setEntities,
      removeEntities,
      removeAllEntities,
      setEntity,
      selectedEntitiesCount,
      isEntitiesOpen,
      toggleEntities,
      openEntities,
      clickableMarkers,
      activeMapTool,
      setActiveMapTool,
      clusteringEnabled,
      isStatusBarOpen,
      toggleStatusBar,
      activeStatusBar,
      cursor,
      hiddenMarkers,
      hasEntityDrawer,
      additionalMapContextMenuItems,
      setAdditionalMapContextMenuItems,
      intersectsWithExtraDeckGlData,
    ]
  );

  return { contextValue: contextValue as MapContextData, extraDeckGlLayers };
};
